<template>
    <DashboardLayout title="Agent Configuration" subtitle="Configure and train your AI Agent.">
        <div v-if="agent" class="split" style="margin-top: -8px">
            <!-- Main Content (Tabs) -->
            <div style="display: flex; flex-direction: column">
                <div class="tabs">
                    <div class="tab" :class="{active: activeTab === 'get_started'}" @click="activeTab = 'get_started'">Get started</div>
                    <div class="tab" :class="{active: activeTab === 'profile'}" @click="activeTab = 'profile'">Profile</div>
                    <div class="tab" :class="{active: activeTab === 'skills'}" @click="activeTab = 'skills'">Skills</div>
                    <div class="tab" :class="{active: activeTab === 'knowledge'}" @click="activeTab = 'knowledge'">Knowledge</div>
                    <div class="tab" :class="{active: activeTab === 'widget'}" @click="activeTab = 'widget'">Widget</div>
                </div>

                <div v-if="activeTab === 'get_started'" style="flex: 1; overflow-y: auto">
                    <h2 style="font-size: 18px; margin-top: 0">Get your AI agent ready for chats</h2>
                    <div class="checklist">
                        <div class="check-item">
                            <div class="check-item-left">
                                <div class="check-icon">✓</div>
                                <span>Create AI agent</span>
                            </div>
                            <span style="color: var(--text-muted)">›</span>
                        </div>
                        <div class="check-item" @click="activeTab = 'knowledge'">
                            <div class="check-item-left">
                                <div class="check-icon">✓</div>
                                <span>Train AI agent with knowledge</span>
                                <span class="badge" style="margin-left: 8px">{{ sources.length }} Sources</span>
                            </div>
                            <span style="color: var(--text-muted)">›</span>
                        </div>
                        <div class="check-item">
                            <div class="check-item-left">
                                <div class="check-icon">✓</div>
                                <span>Ask questions and check answers</span>
                            </div>
                            <span style="color: var(--text-muted)">›</span>
                        </div>
                        <div class="check-item">
                            <div class="check-item-left">
                                <div class="check-icon">✓</div>
                                <span>Connect to your channels</span>
                                <span class="badge" style="margin-left: 8px; background: rgba(16, 185, 129, 0.2)">Live</span>
                            </div>
                            <span style="color: var(--text-muted)">›</span>
                        </div>
                    </div>

                    <div style="margin-top: 32px">
                        <h2 style="font-size: 18px; margin-bottom: 4px">Refine AI agent answers</h2>
                        <p style="color: var(--text-secondary); font-size: 14px; margin-top: 0; margin-bottom: 16px">
                            Increase accuracy and keep answers on brand and aligned with your business goals.
                        </p>
                        <div class="cards-grid">
                            <div class="action-card" @click="activeTab = 'knowledge'">
                                <div class="action-card-icon">📚</div>
                                <h3>Add more sources</h3>
                                <p>Improve accuracy and cover more questions.</p>
                            </div>
                            <div class="action-card" @click="activeTab = 'skills'">
                                <div class="action-card-icon">⚡</div>
                                <h3>Manage skills</h3>
                                <p>Automate tasks and handle complex requests.</p>
                            </div>
                            <div class="action-card" @click="activeTab = 'profile'">
                                <div class="action-card-icon">⚙️</div>
                                <h3>Modify instructions</h3>
                                <p>Control how your AI agent responds and behaves.</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="activeTab === 'profile'" style="flex: 1; overflow-y: auto">
                    <div class="panel">
                        <h2>Profile Details</h2>
                        <form class="form-grid" @submit.prevent="saveAgent">
                            <div class="field"><label>Name</label><input v-model="agent.name" required /></div>
                            <div class="field"><label>Instruction</label><textarea v-model="agent.description"></textarea></div>
                            <div class="field"><label>Welcome message</label><input v-model="agent.welcomeMessage" /></div>
                            <div class="field"><label>Tone</label><input v-model="agent.tone" /></div>
                            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px">
                                <div class="field-unstyle">
                                    <label>Answer provider</label>
                                    <VueSelect
                                        v-model="agent.llmProvider"
                                        :options="llmProviderOptions"
                                        label="label"
                                        :reduce="(option) => option.value"
                                        :clearable="false"
                                        :appendToBody="true"
                                        placeholder="Select answer provider"
                                    />
                                </div>
                                <div class="field-unstyle">
                                    <label>Embedding provider</label>
                                    <VueSelect
                                        v-model="agent.embeddingProvider"
                                        :options="embeddingProviderOptions"
                                        label="label"
                                        :reduce="(option) => option.value"
                                        :clearable="false"
                                        :appendToBody="true"
                                        placeholder="Select embedding provider"
                                    />
                                </div>
                            </div>
                            <label style="margin-top: 8px"><input v-model="agent.isActive" type="checkbox" /> Active Status</label>
                            <div class="gds-settings">
                                <label><input v-model="agent.gdsEnabled" type="checkbox" @change="ensureGdsDefaults(agent)" /> GDS Language / Non-academic</label>
                                <div v-if="agent.gdsEnabled" class="form-grid">
                                    <div class="field">
                                        <label>GDS Type</label>
                                        <div class="radio-row">
                                            <label><input v-model="agent.gdsType" type="radio" value="EducationAgency" /> Education Agency</label>
                                            <label><input v-model="agent.gdsType" type="radio" value="School" /> School</label>
                                        </div>
                                    </div>
                                    <div v-if="agent.gdsType === 'EducationAgency'" class="field">
                                        <label>EduAgencyId</label>
                                        <input v-model.number="agent.eduAgencyId" type="number" min="1" required />
                                    </div>
                                    <div v-else-if="agent.gdsType === 'School'" class="field">
                                        <label>SchoolId</label>
                                        <input v-model.number="agent.schoolId" type="number" min="1" required />
                                    </div>
                                </div>
                                <label><input v-model="agent.gdsAcademicEnabled" type="checkbox" /> GDS Academic</label>
                            </div>
                            <div style="margin-top: 16px">
                                <button class="primary-button">Save Profile Details</button>
                            </div>
                        </form>
                    </div>
                </div>

                <div v-if="activeTab === 'skills'" style="flex: 1; overflow-y: auto">
                    <div class="panel">
                        <div class="toolbar">
                            <h2>Skills</h2>
                            <div style="display: flex; gap: 8px">
                                <button class="ghost-button" @click="loadSkills">Refresh</button>
                                <button class="primary-button" @click="isSkillFormOpen = !isSkillFormOpen">
                                    {{ isSkillFormOpen ? "Close form" : "+ Add new skill" }}
                                </button>
                            </div>
                        </div>
                        <form v-if="isSkillFormOpen" class="form-grid" style="margin-bottom: 20px" @submit.prevent="createSkill">
                            <div class="field"><label>Skill name</label><input v-model="skillForm.name" required placeholder="Academic Program Interest Routing" /></div>
                            <div class="field">
                                <label>Skill instruction</label>
                                <textarea
                                    v-model="skillForm.instruction"
                                    required
                                    placeholder="Describe when this skill should trigger and what the AI should ask or do..."
                                    style="min-height: 130px"
                                ></textarea>
                            </div>
                            <label><input v-model="skillForm.isActive" type="checkbox" /> Active</label>
                            <div>
                                <button class="primary-button" :disabled="isSkillSaving">{{ isSkillSaving ? "Building skill..." : "Create skill" }}</button>
                            </div>
                        </form>

                        <div v-if="skills.length === 0" style="color: var(--text-muted); font-size: 14px; padding: 20px 0">No skills yet.</div>
                        <div v-else style="display: flex; flex-direction: column; gap: 12px">
                            <div v-for="skill in skills" :key="skill.id" style="border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); padding: 16px; background: var(--bg-surface)">
                                <div style="display: flex; justify-content: space-between; gap: 16px; align-items: flex-start">
                                    <div style="min-width: 0; flex: 1">
                                        <div style="display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 6px">
                                            <h3 style="margin: 0; font-size: 15px">{{ skill.name }}</h3>
                                            <button class="ghost-button" type="button" @click="toggleSkillDetails(skill)">
                                                {{ isSkillExpanded(skill) ? "Hide detail" : "Detail" }}
                                            </button>
                                        </div>
                                        <p style="margin: 0; color: var(--text-secondary); font-size: 13px">{{ skill.instruction }}</p>
                                    </div>
                                    <div style="display: flex; gap: 8px; align-items: center">
                                        <span class="badge" :class="{inactive: !skill.isActive}">{{ skill.isActive ? "Active" : "Inactive" }}</span>
                                    </div>
                                </div>
                                <div v-if="isSkillExpanded(skill)" style="margin-top: 14px">
                                    <div style="display: flex; justify-content: flex-end; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 12px">
                                        <button class="ghost-button" @click="openSkillEdit(skill)">Edit</button>
                                        <button class="ghost-button" @click="toggleSkillActive(skill)">{{ skill.isActive ? "Deactivate" : "Activate" }}</button>
                                        <button class="ghost-button" @click="rebuildSkill(skill)">Rebuild</button>
                                        <button class="ghost-button" @click="deleteSkill(skill)">Delete</button>
                                    </div>
                                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px">
                                        <div style="border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); padding: 12px; background: var(--bg-panel)">
                                            <strong style="display: block; font-size: 13px; margin-bottom: 6px">{{ skill.intentName }}</strong>
                                            <p style="margin: 0; color: var(--text-secondary); font-size: 13px">{{ skill.intentDescription }}</p>
                                            <div v-if="skill.triggerConditions?.length" style="display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px">
                                                <span v-for="condition in skill.triggerConditions" :key="condition" class="badge">{{ condition }}</span>
                                            </div>
                                        </div>
                                        <div style="border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); padding: 12px; background: var(--bg-panel)">
                                            <strong style="display: block; font-size: 13px; margin-bottom: 6px">{{ skill.actionName }}</strong>
                                            <span class="badge" style="margin-bottom: 8px">{{ skill.actionType }}</span>
                                            <p style="margin: 0 0 8px; color: var(--text-secondary); font-size: 13px">{{ skill.actionInstruction }}</p>
                                            <div v-if="skill.actionParameters?.length" style="display: flex; flex-direction: column; gap: 6px">
                                                <span v-for="parameter in skill.actionParameters" :key="parameter.key" class="badge"> {{ parameter.key }}{{ parameter.required ? " *" : "" }} </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="panel" style="margin-top: 16px">
                        <div class="toolbar">
                            <h2>Template Skills</h2>
                            <button class="ghost-button" @click="loadSkillTemplates">Refresh</button>
                        </div>
                        <div v-if="templateSkills.length === 0" style="color: var(--text-muted); font-size: 14px; padding: 20px 0">No template skills available.</div>
                        <div v-else style="display: flex; flex-direction: column; gap: 12px">
                            <div
                                v-for="template in templateSkills"
                                :key="template.id"
                                style="border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); padding: 16px; background: var(--bg-surface)"
                            >
                                <div style="display: flex; justify-content: space-between; gap: 16px; align-items: flex-start">
                                    <div>
                                        <div style="display: flex; gap: 8px; align-items: center; margin-bottom: 6px">
                                            <h3 style="margin: 0; font-size: 15px">{{ template.name }}</h3>
                                            <span v-if="template.isDefaultForNewAgents" class="badge">Default</span>
                                        </div>
                                        <p style="margin: 0; color: var(--text-secondary); font-size: 13px">{{ template.instruction }}</p>
                                    </div>
                                    <button class="primary-button" :disabled="isTemplateAdded(template) || templateAddingId === template.id" @click="addTemplateSkill(template)">
                                        {{ isTemplateAdded(template) ? "Added" : templateAddingId === template.id ? "Adding..." : "Add skill" }}
                                    </button>
                                </div>
                                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 14px">
                                    <div style="border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); padding: 12px; background: var(--bg-panel)">
                                        <strong style="display: block; font-size: 13px; margin-bottom: 6px">{{ template.intentName }}</strong>
                                        <p style="margin: 0; color: var(--text-secondary); font-size: 13px">{{ template.intentDescription }}</p>
                                    </div>
                                    <div style="border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); padding: 12px; background: var(--bg-panel)">
                                        <strong style="display: block; font-size: 13px; margin-bottom: 6px">{{ template.actionName }}</strong>
                                        <span class="badge" style="margin-bottom: 8px">{{ template.actionType }}</span>
                                        <div v-if="template.actionParameters?.length" style="display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px">
                                            <span v-for="parameter in template.actionParameters" :key="parameter.key" class="badge"> {{ parameter.key }}{{ parameter.required ? " *" : "" }} </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="activeTab === 'knowledge'" style="flex: 1; overflow-y: auto">
                    <div class="toolbar">
                        <div style="display: flex; gap: 8px; align-items: center">
                            <span class="badge" style="background: var(--bg-surface); color: var(--text-primary)">All ({{ sources.length }})</span>
                            <span class="badge" style="background: transparent; color: var(--text-muted); font-weight: 500"
                                >🌐 Web ({{ sources.filter((s) => s.sourceType === "WebUrl").length }})</span
                            >
                            <span class="badge" style="background: transparent; color: var(--text-muted); font-weight: 500"
                                >📄 Docs ({{ sources.filter((s) => s.sourceType === "Document").length }})</span
                            >
                            <span class="badge" style="background: transparent; color: var(--text-muted); font-weight: 500"
                                >📝 Articles ({{ sources.filter((s) => s.sourceType === "ManualText").length }})</span
                            >
                        </div>
                        <div style="display: flex; gap: 8px">
                            <button class="ghost-button" @click="loadSources">Refresh</button>
                            <button class="primary-button" @click="isNewSourceModalOpen = true">+ New source</button>
                        </div>
                    </div>

                    <div class="table-container">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th>Source</th>
                                    <th>Status</th>
                                    <th>Last Updated</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="source in sources" :key="source.id">
                                    <tr @click="toggleSourceExpanded(source)" style="cursor: pointer">
                                        <td>
                                            <div style="display: flex; align-items: center; gap: 12px">
                                                <span v-if="source.sourceType === 'WebUrl'" style="color: #3b82f6">🌐</span>
                                                <span v-else-if="source.sourceType === 'Document'" style="color: #ec4899">📄</span>
                                                <span v-else style="color: #10b981">📝</span>
                                                <div>
                                                    <div style="font-weight: 500; color: var(--text-primary)">{{ source.title }}</div>
                                                    <div style="font-size: 12px; color: var(--text-secondary)">
                                                        {{ source.sourceType }} {{ source.url ? " - " + source.url : "" }}
                                                        <span v-if="source.sourceType === 'WebUrl'"> - {{ source.ingestedPageCount || 0 }} pages indexed</span>
                                                        <span v-if="source.sourceType === 'WebUrl' && source.discoveredPageCount"> - {{ source.discoveredPageCount }} pages discovered</span>
                                                    </div>
                                                    <KnowledgeSchoolSelect :model-value="source.schoolId" :schools="knowledgeSchools" :disabled="savingSchoolIds.has(source.id)" @update:modelValue="updateSourceSchool(source, $event)" />
                                                    <small v-if="schoolErrors[source.id]" role="alert" style="color: #b91c1c">{{ schoolErrors[source.id] }}</small>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span
                                                class="badge"
                                                :style="
                                                    source.status === 'Completed'
                                                        ? {background: 'rgba(16,185,129,0.15)', color: '#10b981'}
                                                        : source.status === 'Failed'
                                                          ? {background: 'rgba(239,68,68,0.15)', color: '#ef4444'}
                                                          : {background: 'rgba(234,179,8,0.15)', color: '#eab308'}
                                                "
                                                >{{ source.status }}</span
                                            >
                                        </td>
                                        <td style="color: var(--text-secondary)">{{ new Date(source.updatedAt).toLocaleDateString() }}</td>
                                        <td style="text-align: right">
                                            <button class="ghost-button" @click.stop="deleteSource(source)">Delete</button>
                                        </td>
                                    </tr>
                                    <tr v-if="expandedSourceId === source.id" style="background: var(--bg-surface)">
                                        <td colspan="4" style="padding: 0">
                                            <div style="padding: 16px 24px; border-top: 1px solid var(--border-subtle)">
                                                <div v-if="discoveredPages.length > 0" style="margin-bottom: 20px">
                                                    <div style="display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px">
                                                        <h4 style="margin: 0; font-size: 13px; color: var(--text-muted); text-transform: uppercase">Discovered Pages</h4>
                                                        <div style="display: flex; align-items: center; gap: 8px">
                                                            <input v-model="discoveredPageSearch" type="search" placeholder="Search title or URL" style="max-width: 260px" />
                                                            <button
                                                                class="primary-button"
                                                                :disabled="selectedDiscoveredPageCount === 0 || isIndexingSelectedPages"
                                                                @click="indexSelectedDiscoveredPages(source)"
                                                            >
                                                                Index selected ({{ selectedDiscoveredPageCount }})
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div v-if="isLoadingDiscoveredPages" style="color: var(--text-secondary); font-size: 13px">Loading discovered pages...</div>
                                                    <div v-else-if="filteredDiscoveredPages.length === 0" style="color: var(--text-secondary); font-size: 13px">
                                                        No discovered pages match your search.
                                                    </div>
                                                    <div v-else style="display: flex; flex-direction: column; gap: 8px">
                                                        <label
                                                            v-for="page in filteredDiscoveredPages"
                                                            :key="page.id"
                                                            style="
                                                                display: flex;
                                                                align-items: center;
                                                                gap: 12px;
                                                                padding: 12px 16px;
                                                                background: var(--bg-panel);
                                                                border: 1px solid var(--border-subtle);
                                                                border-radius: 6px;
                                                            "
                                                        >
                                                            <input v-model="page.isSelected" type="checkbox" />
                                                            <div style="display: flex; flex-direction: column; gap: 4px; overflow: hidden">
                                                                <span style="font-weight: 500; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis">{{
                                                                    page.title || page.url
                                                                }}</span>
                                                                <span style="font-size: 12px; color: var(--text-secondary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis">{{
                                                                    page.url
                                                                }}</span>
                                                            </div>
                                                        </label>
                                                    </div>
                                                </div>
                                                <div style="display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px">
                                                    <h4 style="margin: 0; font-size: 13px; color: var(--text-muted); text-transform: uppercase">Ingested Pages</h4>
                                                    <input v-if="ingestedPages.length > 0" v-model="ingestedPageSearch" type="search" placeholder="Search title or URL" style="max-width: 260px" />
                                                </div>
                                                <div v-if="isLoadingPages" style="color: var(--text-secondary); font-size: 13px">Loading pages...</div>
                                                <div v-else-if="ingestedPages.length === 0" style="color: var(--text-secondary); font-size: 13px">No pages ingested yet.</div>
                                                <div v-else-if="filteredIngestedPages.length === 0" style="color: var(--text-secondary); font-size: 13px">No pages match your search.</div>
                                                <div v-else style="display: flex; flex-direction: column; gap: 8px">
                                                    <div
                                                        v-for="page in filteredIngestedPages"
                                                        :key="page.id"
                                                        @click="openPageDetails(page)"
                                                        style="
                                                            display: flex;
                                                            justify-content: space-between;
                                                            align-items: center;
                                                            padding: 12px 16px;
                                                            background: var(--bg-panel);
                                                            border: 1px solid var(--border-subtle);
                                                            border-radius: 6px;
                                                            cursor: pointer;
                                                        "
                                                    >
                                                        <div style="display: flex; flex-direction: column; gap: 4px; overflow: hidden">
                                                            <span style="font-weight: 500; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis">{{
                                                                page.title || page.url || "Untitled"
                                                            }}</span>
                                                            <span style="font-size: 12px; color: var(--text-secondary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis">{{
                                                                page.url
                                                            }}</span>
                                                        </div>
                                                        <div style="display: flex; gap: 16px; align-items: center; font-size: 13px; color: var(--text-muted)">
                                                            <span>{{ page.chunkCount }} chunks</span>
                                                            <span>{{ new Date(page.createdAt).toLocaleDateString() }}</span>
                                                            <span style="color: var(--accent-primary)">View ›</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                                <tr v-if="sources.length === 0">
                                    <td colspan="4" style="text-align: center; color: var(--text-muted); padding: 32px">No sources added yet.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="activeTab === 'widget'" style="flex: 1; overflow-y: auto">
                    <div class="panel">
                        <div class="toolbar">
                            <h2>Widgets</h2>
                            <div style="display: flex; gap: 8px">
                                <button class="ghost-button" @click="copyWidgetScript">{{ isWidgetScriptCopied ? "Copied" : "Copy script" }}</button>
                                <button class="primary-button" @click="isWidgetFormOpen = !isWidgetFormOpen">
                                    {{ isWidgetFormOpen ? "Close form" : "+ Add new widget" }}
                                </button>
                            </div>
                        </div>
                        <form v-if="isWidgetFormOpen" class="form-grid" style="margin-bottom: 20px" @submit.prevent="createWidget">
                            <div class="field">
                                <label>Widget name</label>
                                <input v-model="newWidgetName" required placeholder="Main website widget" />
                            </div>
                            <div>
                                <button class="primary-button" :disabled="isWidgetSaving">{{ isWidgetSaving ? "Adding..." : "Add widget" }}</button>
                            </div>
                        </form>
                        <div class="table-container" style="margin-bottom: 20px">
                            <table class="table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Status</th>
                                        <th>Created</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="widget in widgets" :key="widget.id">
                                        <td>{{ widget.name }}</td>
                                        <td>
                                            <span
                                                class="badge"
                                                :style="widget.isActive ? {background: 'rgba(16,185,129,0.15)', color: '#10b981'} : {background: 'rgba(239,68,68,0.15)', color: '#ef4444'}"
                                                >{{ widget.isActive ? "Active" : "Inactive" }}</span
                                            >
                                        </td>
                                        <td style="color: var(--text-secondary)">{{ new Date(widget.createdAt).toLocaleDateString() }}</td>
                                        <td style="text-align: right">
                                            <div style="display: flex; gap: 8px; justify-content: flex-end">
                                                <button class="ghost-button" type="button" @click="copyWidgetScriptFor(widget)">{{ copiedWidgetId === widget.id ? "Copied" : "Copy script" }}</button>
                                                <button class="ghost-button" type="button" @click="openWidgetConfiguration(widget)">Configure</button>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-if="widgets.length === 0">
                                        <td colspan="4" style="text-align: center; color: var(--text-muted); padding: 32px">No widgets added yet.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Chat Panel (Right Side) -->
            <aside class="chat-panel" style="--bg-panel: #f8f8f8">
                <div class="chat-sim-header">
                    <div class="chat-sim-header-top">
                        <h2 class="chat-sim-title">Chat Simulation</h2>
                        <button class="icon-button" @click="resetChat" title="Reset Chat">↻</button>
                    </div>
                    <p class="chat-sim-desc">Test this AI agent with the below chat simulator to check how well it exhibits the desired skills and knowledge base.</p>
                </div>
                <div class="chat-body" ref="chatBody">
                    <!-- Welcome message -->
                    <div class="chat-message bot">
                        <div class="chat-message-avatar bot-avatar">
                            <img v-if="companyLogoUrl" :src="companyLogoUrl" alt="Company logo" class="bot-avatar-image" @error="handleCompanyLogoError" />
                            <span v-else>{{ botAvatarFallback }}</span>
                        </div>
                        <div class="chat-message-data">
                            <div class="chat-bubble" v-html="renderMarkdown(agent.welcomeMessage || 'Hello! How can I help you today?')"></div>
                        </div>
                    </div>
                    <!-- Conversation messages -->
                    <div v-for="(msg, i) in chatMessages" :key="i" class="chat-message" :class="{bot: msg.role === 'bot', user: msg.role === 'user'}">
                        <div v-if="msg.role === 'bot'" class="chat-message-avatar bot-avatar">
                            <img v-if="companyLogoUrl" :src="companyLogoUrl" alt="Company logo" class="bot-avatar-image" @error="handleCompanyLogoError" />
                            <span v-else>{{ botAvatarFallback }}</span>
                        </div>
                        <div class="chat-message-data" :class="{'has-programs': hasProgramCards(msg)}">
                            <div class="chat-bubble" :class="{'has-programs': hasProgramCards(msg)}" v-html="renderChatMessage(msg)" @click="handleProgramDescriptionToggle"></div>
                        </div>
                    </div>
                    <!-- Thinking indicator -->
                    <div v-if="isChatLoading" class="chat-message bot">
                        <div class="chat-message-avatar bot-avatar">
                            <img v-if="companyLogoUrl" :src="companyLogoUrl" alt="Company logo" class="bot-avatar-image" @error="handleCompanyLogoError" />
                            <span v-else>{{ botAvatarFallback }}</span>
                        </div>
                        <div class="chat-message-data">
                            <div class="chat-bubble thinking">Thinking<span class="dots"></span></div>
                        </div>
                    </div>
                </div>
                <div class="chat-input-area">
                    <div class="chat-input-box">
                        <input type="text" v-model="chatInput" @keyup.enter="sendMessage" :disabled="isChatLoading" placeholder="Ask a question..." />
                        <button class="chat-send-btn" @click="sendMessage" :disabled="!chatInput.trim() || isChatLoading">↑</button>
                    </div>
                </div>
            </aside>
        </div>

        <!-- New Source Modal -->
        <div v-if="isNewSourceModalOpen" class="modal-overlay" @click.self="isNewSourceModalOpen = false">
            <div class="modal">
                <div class="modal-header">
                    <h2>Add New Source</h2>
                    <button class="icon-button" @click="isNewSourceModalOpen = false">✕</button>
                </div>
                <div class="modal-body">
                    <div class="tabs" style="margin-bottom: 16px">
                        <div class="tab" :class="{active: newSourceTab === 'web'}" @click="newSourceTab = 'web'">Web URL</div>
                        <div class="tab" :class="{active: newSourceTab === 'doc'}" @click="newSourceTab = 'doc'">Document</div>
                        <div class="tab" :class="{active: newSourceTab === 'article'}" @click="newSourceTab = 'article'">Article</div>
                    </div>

                    <form v-if="newSourceTab === 'web'" id="source-form-web" class="form-grid" @submit.prevent="createWebSource">
                        <KnowledgeSchoolSelect v-model="newSourceSchoolId" :schools="knowledgeSchools" />
                        <div class="field"><label>URL</label><input v-model="webForm.url" required type="url" placeholder="https://example.com" /></div>
                        <div class="field"><label>Title (Optional)</label><input v-model="webForm.title" placeholder="e.g. Company Website" /></div>
                        <div class="field">
                            <label>Crawl Depth</label>
                            <div style="display: flex; gap: 16px; margin-bottom: 8px">
                                <label style="display: flex; align-items: center; gap: 4px; font-weight: normal; cursor: pointer">
                                    <input type="radio" v-model="webForm.crawlMode" value="all" name="crawlMode" /> All website
                                </label>
                                <label style="display: flex; align-items: center; gap: 4px; font-weight: normal; cursor: pointer">
                                    <input type="radio" v-model="webForm.crawlMode" value="page" name="crawlMode" /> Page only
                                </label>
                                <label style="display: flex; align-items: center; gap: 4px; font-weight: normal; cursor: pointer">
                                    <input type="radio" v-model="webForm.crawlMode" value="custom" name="crawlMode" /> Custom
                                </label>
                            </div>
                            <div v-if="webForm.crawlMode === 'custom'">
                                <input v-model.number="webForm.customCrawlLimit" type="number" min="1" placeholder="e.g. 50" required />
                                <p style="font-size: 12px; color: var(--text-muted); margin: 4px 0 0 0">Enter the maximum number of pages to crawl.</p>
                            </div>
                        </div>
                        <label style="display: flex; align-items: center; gap: 8px; font-weight: normal; cursor: pointer">
                            <input v-model="webForm.reviewBeforeIndex" type="checkbox" />
                            Review pages before indexing
                        </label>
                    </form>

                    <form v-else-if="newSourceTab === 'doc'" id="source-form-doc" class="form-grid" @submit.prevent="createDocSource">
                        <div class="field">
                            <label>Document File</label
                            ><input
                                type="file"
                                ref="docFileInput"
                                required
                                accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.md,.csv"
                                style="padding: 8px; background: transparent; border: 1px dashed var(--border-strong)"
                            />
                        </div>
                        <KnowledgeSchoolSelect v-model="newSourceSchoolId" :schools="knowledgeSchools" />
                        <div class="field"><label>Title (Optional)</label><input v-model="docForm.title" placeholder="e.g. Employee Handbook" /></div>
                        <p style="font-size: 12px; color: var(--text-muted); margin: 0">Supported: PDF, DOC, DOCX, PPT, PPTX, TXT, MD, CSV.</p>
                    </form>

                    <form v-else-if="newSourceTab === 'article'" id="source-form-article" class="form-grid" @submit.prevent="createArticleSource">
                        <KnowledgeSchoolSelect v-model="newSourceSchoolId" :schools="knowledgeSchools" />
                        <div class="field"><label>Title (Optional)</label><input v-model="articleForm.title" placeholder="e.g. Pricing Rules" /></div>
                        <div class="field">
                            <label>Content</label><textarea v-model="articleForm.content" required placeholder="Paste your article or raw text here..." style="min-height: 150px"></textarea>
                        </div>
                    </form>
                </div>
                <div class="modal-footer">
                    <button class="ghost-button" @click="isNewSourceModalOpen = false">Cancel</button>
                    <button v-if="newSourceTab === 'web'" type="submit" form="source-form-web" class="primary-button">{{ webForm.reviewBeforeIndex ? "Discover Pages" : "Add URL" }}</button>
                    <button v-else-if="newSourceTab === 'doc'" type="submit" form="source-form-doc" class="primary-button">Upload Document</button>
                    <button v-else-if="newSourceTab === 'article'" type="submit" form="source-form-article" class="primary-button">Save Article</button>
                </div>
            </div>
        </div>

        <!-- Edit Skill Modal -->
        <div v-if="editingSkill" class="modal-overlay" @click.self="closeSkillEdit">
            <div class="modal" style="width: min(860px, 92vw)">
                <div class="modal-header">
                    <h2>Edit Skill</h2>
                    <button class="icon-button" @click="closeSkillEdit">✕</button>
                </div>
                <div class="modal-body">
                    <form id="skill-edit-form" class="form-grid" @submit.prevent="saveSkillEdit">
                        <div class="field"><label>action_name</label><input v-model="skillEditForm.actionName" required /></div>
                        <div class="field">
                            <label>intent_description</label>
                            <textarea v-model="skillEditForm.intentDescription" required style="min-height: 100px"></textarea>
                        </div>
                        <div class="field">
                            <label>action_instruction</label>
                            <textarea v-model="skillEditForm.actionInstruction" required style="min-height: 120px"></textarea>
                        </div>
                        <div class="field">
                            <label>trigger_conditions_json</label>
                            <textarea v-model="skillEditForm.triggerConditionsJson" required style="min-height: 130px; font-family: monospace; font-size: 13px"></textarea>
                        </div>
                        <div class="field">
                            <label>action_parameters_json</label>
                            <textarea v-model="skillEditForm.actionParametersJson" required style="min-height: 160px; font-family: monospace; font-size: 13px"></textarea>
                        </div>
                        <p v-if="skillEditError" style="color: #dc2626; font-size: 13px; margin: 0">{{ skillEditError }}</p>
                    </form>
                </div>
                <div class="modal-footer">
                    <button class="ghost-button" @click="closeSkillEdit">Cancel</button>
                    <button type="submit" form="skill-edit-form" class="primary-button" :disabled="isSkillEditSaving">
                        {{ isSkillEditSaving ? "Saving..." : "Save" }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Ingested Page Details Modal -->
        <div v-if="selectedPage" class="modal-overlay" @click.self="selectedPage = null">
            <div class="modal" style="width: min(800px, 90vw)">
                <div class="modal-header">
                    <h2>Page Details</h2>
                    <button class="icon-button" @click="selectedPage = null">✕</button>
                </div>
                <div class="modal-body">
                    <div
                        style="
                            display: grid;
                            grid-template-columns: repeat(2, 1fr);
                            gap: 16px;
                            margin-bottom: 24px;
                            background: var(--bg-surface);
                            padding: 16px;
                            border-radius: var(--radius-sm);
                            border: 1px solid var(--border-subtle);
                        "
                    >
                        <div>
                            <div style="font-size: 12px; color: var(--text-muted); text-transform: uppercase">Title</div>
                            <div style="font-size: 14px; font-weight: 500">{{ selectedPage.title || "N/A" }}</div>
                        </div>
                        <div>
                            <div style="font-size: 12px; color: var(--text-muted); text-transform: uppercase">URL</div>
                            <div style="font-size: 14px; word-break: break-all">{{ selectedPage.url || "N/A" }}</div>
                        </div>
                        <div>
                            <div style="font-size: 12px; color: var(--text-muted); text-transform: uppercase">Ingested At</div>
                            <div style="font-size: 14px">{{ new Date(selectedPage.createdAt).toLocaleString() }}</div>
                        </div>
                        <div>
                            <div style="font-size: 12px; color: var(--text-muted); text-transform: uppercase">Generated Chunks</div>
                            <div style="font-size: 14px; font-weight: 600; color: var(--accent-primary)">{{ selectedPage.chunkCount }}</div>
                        </div>
                    </div>

                    <div class="field">
                        <label>Extracted Content (Before Chunking)</label>
                        <textarea
                            readonly
                            :value="selectedPage.extractedContent"
                            style="min-height: 300px; font-family: monospace; font-size: 13px; line-height: 1.5; background: var(--bg-base); cursor: default"
                        ></textarea>
                    </div>
                </div>
            </div>
        </div>
    </DashboardLayout>
</template>

<script setup>
import {useAuthStore} from "../stores/auth";
import {computed, onMounted, reactive, ref, nextTick} from "vue";
import {useRoute, useRouter} from "vue-router";
import DashboardLayout from "../layouts/DashboardLayout.vue";
import KnowledgeSchoolSelect from "../components/KnowledgeSchoolSelect.vue";
import {decodeToken} from "../utils/jwt";
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import {marked} from "marked";
import DOMPurify from "dompurify";
import {api} from "../services/api";
import {useWidgetConfigurationStore} from "../stores/widgetConfiguration";
import {renderLanguageProgramMessage} from "../../../shared/language-program-cards.mjs";

function renderMarkdown(text) {
    if (!text) return "";
    return DOMPurify.sanitize(marked.parse(text));
}

function renderChatMessage(message) {
    if (Array.isArray(message?.languagePrograms) && message.languagePrograms.length > 0)
        return DOMPurify.sanitize(renderLanguageProgramMessage(message.content, message.languagePrograms));
    const programs = Array.isArray(message?.academicPrograms) ? message.academicPrograms : [];
    const content = message?.content || "";
    if (!content.includes("{#programlistregion}") || programs.length === 0) {
        return renderMarkdown(content);
    }

    const [before = "", after = ""] = content.split("{#programlistregion}");
    const html = [
        before.trim() ? `<div class="program-message-text">${marked.parseInline(before.trim())}</div>` : "",
        `<div class="program-card-list">${programs.map(renderProgramCard).join("")}</div>`,
        after.trim() ? `<div class="program-message-text">${marked.parseInline(after.trim())}</div>` : "",
    ].join("");

    return DOMPurify.sanitize(html, {
        ADD_ATTR: ["data-reference-id", "data-full", "data-short", "data-expanded", "target", "rel"],
    });
}

function hasProgramCards(message) {
    if (Array.isArray(message?.languagePrograms) && message.languagePrograms.length > 0) return true;
    return Boolean(message?.content?.includes("{#programlistregion}") && Array.isArray(message?.academicPrograms) && message.academicPrograms.length > 0);
}

function renderProgramCard(program) {
    const location = [program.CityName, program.StateName, program.CountryName].filter(Boolean).join(", ");
    const details = [
        renderProgramDetail("Level", program.LevelName),
        renderProgramDetail("Faculty", program.Discipline),
        program.OpportunitiyString ? `<div class="program-detail program-opportunity"><strong>${escapeHtml(program.OpportunitiyString)}</strong></div>` : "",
        program.TermsString ? `<div class="program-detail">${escapeHtml(program.TermsString)}</div>` : "",
    ].join("");
    const logo = program.SchLogo
        ? `<img src="${escapeAttr(program.SchLogo)}" alt="${escapeAttr(program.SchoolName || "School logo")}" class="program-logo-image" loading="lazy">`
        : `<div class="program-logo-placeholder">${escapeHtml(getSchoolInitials(program.SchoolName))}</div>`;

    return `
        <div class="program-code-block">
            <div class="program-link program-logo">
                ${logo}
                <div class="program-logo-number">${escapeHtml(program.SchoolTypeName || "Program")}</div>
                <div class="program-logo-name">
                    <span class="program-school-name">${escapeHtml(program.SchoolName || "")}</span>
                    <span class="program-location">${escapeHtml(location)}</span>
                </div>
            </div>
            <div class="program-name">${escapeHtml(program.ProgramName || "")}</div>
            ${details}
            ${renderProgramDescription(program.DescriptionShort)}
            <a class="program-link-btn" href="#" data-reference-id="${escapeAttr(program.ProgramId || "")}">Visit Program ↗</a>
        </div>
    `;
}

function renderProgramDescription(value) {
    if (!value) return "";
    const description = String(value).trim();
    const shortText = truncateDescription(description);
    if (description.length <= shortText.length) {
        return `<div class="program-description">"${escapeHtml(description)}"</div>`;
    }

    return `<div class="program-description" data-full="${escapeAttr(description)}" data-short="${escapeAttr(shortText)}" data-expanded="false"><span>"${escapeHtml(shortText)}</span><a href="#" class="program-description-toggle">...more</a></div>`;
}

function truncateDescription(value) {
    if (value.length <= 150) return value;
    const sliced = value.slice(0, 140);
    const lastSpace = sliced.lastIndexOf(" ");
    return (lastSpace >= 100 ? sliced.slice(0, lastSpace) : sliced).trim();
}

function handleProgramDescriptionToggle(event) {
    const toggle = event.target.closest?.(".program-description-toggle");
    if (!toggle) return;
    event.preventDefault();
    const description = toggle.closest(".program-description");
    if (!description) return;

    const expanded = description.dataset.expanded === "true";
    description.dataset.expanded = expanded ? "false" : "true";
    const text = description.querySelector("span");
    if (text) {
        text.textContent = expanded ? `"${description.dataset.short || ""}` : `"${description.dataset.full || ""}"`;
    }
    toggle.textContent = expanded ? "...more" : " less";
}

function getSchoolInitials(name) {
    return String(name || "School")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
}

function renderProgramDetail(label, value) {
    return value ? `<div class="program-detail">• <strong>${escapeHtml(label)}</strong>: ${escapeHtml(value)}</div>` : "";
}

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

function escapeAttr(value) {
    return escapeHtml(value).replace(/`/g, "&#096;");
}

const llmProviderOptions = [
    {label: "OpenAI", value: "OpenAI"},
    {label: "Gemini 2.5 Flash", value: "Gemini25Flash"},
    {label: "Gemini 3 Flash", value: "Gemini3Flash"},
];

const embeddingProviderOptions = [
    {label: "OpenAI", value: "OpenAI"},
    {label: "Gemini", value: "Gemini"},
];

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const widgetConfigurationStore = useWidgetConfigurationStore();
const agent = ref(null);
const activeTab = ref("get_started");
const companyLogoBroken = ref(false);

const decodedUser = computed(() => {
    if (!auth.accessToken) return null;
    return decodeToken(auth.accessToken);
});

const currentUser = computed(() => auth.user || decodedUser.value);

const companyLogoUrl = computed(() => {
    if (companyLogoBroken.value) return null;
    return currentUser.value?.companyLogoUrl || null;
});

const botAvatarFallback = computed(() => (agent.value?.name ? agent.value.name.substring(0, 1).toUpperCase() : "A"));

const skills = ref([]);
const templateSkills = ref([]);
const isSkillSaving = ref(false);
const isSkillFormOpen = ref(false);
const templateAddingId = ref(null);
const skillForm = reactive({name: "", instruction: "", isActive: true});
const expandedSkillIds = ref(new Set());
const editingSkill = ref(null);
const isSkillEditSaving = ref(false);
const skillEditError = ref("");
const skillEditForm = reactive({
    actionName: "",
    intentDescription: "",
    actionInstruction: "",
    triggerConditionsJson: "[]",
    actionParametersJson: "[]",
});

const sources = ref([]);
const expandedSourceId = ref(null);
const ingestedPages = ref([]);
const ingestedPageSearch = ref("");
const isLoadingPages = ref(false);
const discoveredPages = ref([]);
const discoveredPageSearch = ref("");
const isLoadingDiscoveredPages = ref(false);
const isIndexingSelectedPages = ref(false);

const isNewSourceModalOpen = ref(false);
const newSourceTab = ref("web");
const selectedPage = ref(null);

const webForm = reactive({url: "", title: "", crawlMode: "all", customCrawlLimit: null, reviewBeforeIndex: false});
const docFileInput = ref(null);
const docForm = reactive({title: ""});
const knowledgeSchools = ref([]);
const newSourceSchoolId = ref(null);
const savingSchoolIds = reactive(new Set());
const schoolErrors = reactive({});
const articleForm = reactive({title: "", content: ""});

const filteredIngestedPages = computed(() => {
    const query = ingestedPageSearch.value.trim().toLowerCase();
    if (!query) return ingestedPages.value;
    return ingestedPages.value.filter((page) => `${page.title || ""} ${page.url || ""}`.toLowerCase().includes(query));
});

const filteredDiscoveredPages = computed(() => {
    const query = discoveredPageSearch.value.trim().toLowerCase();
    if (!query) return discoveredPages.value;
    return discoveredPages.value.filter((page) => `${page.title || ""} ${page.url || ""}`.toLowerCase().includes(query));
});

const selectedDiscoveredPageCount = computed(() => discoveredPages.value.filter((page) => page.isSelected).length);

const widgetScript = computed(() => {
    const widgetOrigin = import.meta.env.VITE_WIDGET_ORIGIN || "https://wearereadyforai.com";
    const apiBaseUrl = import.meta.env.VITE_WIDGET_API_BASE_URL || `${widgetOrigin}/api`;
    const widgetScriptUrl = `${widgetOrigin}/widget/education-agents-widget.js`;

    return `<script>
  window.EducationAgentsWidgetConfig = {
    apiBaseUrl: "${apiBaseUrl}",
    agentId: "${route.params.id}"
  };
<\/script>
<script src="${widgetScriptUrl}"><\/script>`;
});

const isWidgetScriptCopied = ref(false);
const copiedWidgetId = ref(null);
const widgets = ref([]);
const newWidgetName = ref("");
const isWidgetFormOpen = ref(false);
const isWidgetSaving = ref(false);

// Chat Panel State
const chatMessages = ref([]);
const chatInput = ref("");
const chatSessionId = ref(null);
const chatVisitorId = ref(crypto.randomUUID());
const isChatLoading = ref(false);
const chatBody = ref(null);
const geolocationPromise = loadGeolocation();

function handleCompanyLogoError() {
    companyLogoBroken.value = true;
}

async function load() {
    agent.value = (await api.get(`/ai-agents/${route.params.id}`)).data;
    ensureGdsDefaults(agent.value);
    await Promise.all([loadSources(), loadSkills(), loadSkillTemplates(), loadWidgets()]);
}

async function loadSources() {
    const base = `/ai-agents/${route.params.id}/knowledge-base`;
    const [sourceResponse, schoolResponse] = await Promise.all([api.get(base), api.get(`${base}/schools`)]);
    sources.value = sourceResponse.data;
    knowledgeSchools.value = schoolResponse.data;
}

async function updateSourceSchool(source, schoolId) {
    savingSchoolIds.add(source.id);
    delete schoolErrors[source.id];
    try {
        await api.put(`/ai-agents/${route.params.id}/knowledge-base/${source.id}/school`, {schoolId});
        source.schoolId = schoolId;
    } catch {
        schoolErrors[source.id] = "School selection could not be saved. Please try again.";
    } finally { savingSchoolIds.delete(source.id); }
}

async function loadSkills() {
    skills.value = (await api.get(`/ai-agents/${route.params.id}/skills`)).data;
}

async function loadSkillTemplates() {
    templateSkills.value = (await api.get("/ai-agents/skill-templates")).data;
}

async function loadWidgets() {
    widgets.value = (await api.get(`/ai-agents/${route.params.id}/widgets`)).data;
}

async function copyWidgetScript() {
    await navigator.clipboard.writeText(widgetScript.value);
    isWidgetScriptCopied.value = true;
    window.setTimeout(() => {
        isWidgetScriptCopied.value = false;
    }, 1600);
}

function getWidgetScript(widget) {
    const widgetOrigin = import.meta.env.VITE_WIDGET_ORIGIN || "https://wearereadyforai.com";
    const apiBaseUrl = import.meta.env.VITE_WIDGET_API_BASE_URL || `${widgetOrigin}/api`;
    const widgetScriptUrl = `${widgetOrigin}/widget/education-agents-widget.js`;
    return `<script>
  window.EducationAgentsWidgetConfig = {
    apiBaseUrl: "${apiBaseUrl}",
    agentId: "${route.params.id}",
    widgetId: "${widget.id}"
  };
<\/script>
<script src="${widgetScriptUrl}"><\/script>`;
}

async function copyWidgetScriptFor(widget) {
    await navigator.clipboard.writeText(getWidgetScript(widget));
    copiedWidgetId.value = widget.id;
    window.setTimeout(() => {
        copiedWidgetId.value = null;
    }, 1600);
}

async function createWidget() {
    const name = newWidgetName.value.trim();
    if (!name || isWidgetSaving.value) return;

    isWidgetSaving.value = true;
    try {
        await api.post(`/ai-agents/${route.params.id}/widgets`, {name});
        newWidgetName.value = "";
        isWidgetFormOpen.value = false;
        await loadWidgets();
    } finally {
        isWidgetSaving.value = false;
    }
}

function openWidgetConfiguration(widget) {
    if (!widget?.id) return;

    widgetConfigurationStore.setContext({
        aiAgentId: route.params.id,
        widgetId: widget.id,
        widgetName: widget.name || "",
        isActive: widget.isActive,
    });

    router.push({
        path: "/ai-widget-configuration",
        query: {
            aiAgentId: route.params.id,
            widgetId: widget.id,
        },
    });
}

function isTemplateAdded(template) {
    return skills.value.some((skill) => skill.templateSkillId === template.id);
}

async function addTemplateSkill(template) {
    if (isTemplateAdded(template) || templateAddingId.value) return;
    templateAddingId.value = template.id;
    try {
        await api.post(`/ai-agents/${route.params.id}/skills/from-template/${template.id}`);
        await loadSkills();
    } finally {
        templateAddingId.value = null;
    }
}

async function createSkill() {
    if (!skillForm.name.trim() || !skillForm.instruction.trim()) return;
    isSkillSaving.value = true;
    try {
        await api.post(`/ai-agents/${route.params.id}/skills`, skillForm);
        Object.assign(skillForm, {name: "", instruction: "", isActive: true});
        isSkillFormOpen.value = false;
        await loadSkills();
    } finally {
        isSkillSaving.value = false;
    }
}

async function toggleSkillActive(skill) {
    await api.put(`/ai-agents/${route.params.id}/skills/${skill.id}`, {
        name: skill.name,
        instruction: skill.instruction,
        isActive: !skill.isActive,
        rebuildDefinition: false,
    });
    await loadSkills();
}

async function rebuildSkill(skill) {
    await api.put(`/ai-agents/${route.params.id}/skills/${skill.id}`, {
        name: skill.name,
        instruction: skill.instruction,
        isActive: skill.isActive,
        rebuildDefinition: true,
    });
    await loadSkills();
}

function isSkillExpanded(skill) {
    return expandedSkillIds.value.has(skill.id);
}

function toggleSkillDetails(skill) {
    const next = new Set(expandedSkillIds.value);
    if (next.has(skill.id)) {
        next.delete(skill.id);
    } else {
        next.add(skill.id);
    }
    expandedSkillIds.value = next;
}

function openSkillEdit(skill) {
    editingSkill.value = skill;
    skillEditError.value = "";
    Object.assign(skillEditForm, {
        actionName: skill.actionName || "",
        intentDescription: skill.intentDescription || "",
        actionInstruction: skill.actionInstruction || "",
        triggerConditionsJson: JSON.stringify(skill.triggerConditions || [], null, 2),
        actionParametersJson: JSON.stringify(skill.actionParameters || [], null, 2),
    });
}

function closeSkillEdit() {
    editingSkill.value = null;
    skillEditError.value = "";
}

async function saveSkillEdit() {
    const skill = editingSkill.value;
    if (!skill || isSkillEditSaving.value) return;

    if (!isJsonArray(skillEditForm.triggerConditionsJson)) {
        skillEditError.value = "trigger_conditions_json must be a valid JSON array.";
        return;
    }

    if (!isJsonArray(skillEditForm.actionParametersJson)) {
        skillEditError.value = "action_parameters_json must be a valid JSON array.";
        return;
    }

    isSkillEditSaving.value = true;
    skillEditError.value = "";
    try {
        await api.put(`/ai-agents/${route.params.id}/skills/${skill.id}`, {
            name: skill.name,
            instruction: skill.instruction,
            isActive: skill.isActive,
            rebuildDefinition: false,
            intentDescription: skillEditForm.intentDescription,
            triggerConditionsJson: normalizeJson(skillEditForm.triggerConditionsJson),
            actionName: skillEditForm.actionName,
            actionInstruction: skillEditForm.actionInstruction,
            actionParametersJson: normalizeJson(skillEditForm.actionParametersJson),
        });
        closeSkillEdit();
        await loadSkills();
    } catch (error) {
        skillEditError.value = error?.response?.data || "Skill could not be saved.";
    } finally {
        isSkillEditSaving.value = false;
    }
}

function isJsonArray(value) {
    try {
        return Array.isArray(JSON.parse(value));
    } catch {
        return false;
    }
}

function normalizeJson(value) {
    return JSON.stringify(JSON.parse(value));
}

async function deleteSkill(skill) {
    if (!confirm(`Delete skill "${skill.name}"?`)) return;
    await api.delete(`/ai-agents/${route.params.id}/skills/${skill.id}`);
    await loadSkills();
}

async function toggleSourceExpanded(source) {
    if (expandedSourceId.value === source.id) {
        expandedSourceId.value = null;
        ingestedPages.value = [];
        ingestedPageSearch.value = "";
        discoveredPages.value = [];
        discoveredPageSearch.value = "";
    } else {
        expandedSourceId.value = source.id;
        ingestedPageSearch.value = "";
        discoveredPageSearch.value = "";
        isLoadingPages.value = true;
        isLoadingDiscoveredPages.value = source.discoveredPageCount > 0;
        try {
            const requests = [api.get(`/ai-agents/${route.params.id}/knowledge-base/${source.id}/ingested-pages`)];
            if (source.discoveredPageCount > 0) {
                requests.push(api.get(`/ai-agents/${route.params.id}/knowledge-base/${source.id}/discovered-pages`));
            }
            const [ingestedResponse, discoveredResponse] = await Promise.all(requests);
            ingestedPages.value = ingestedResponse.data;
            discoveredPages.value = discoveredResponse?.data || [];
        } finally {
            isLoadingPages.value = false;
            isLoadingDiscoveredPages.value = false;
        }
    }
}

function openPageDetails(page) {
    selectedPage.value = page;
}

async function deleteSource(source) {
    if (!confirm(`Delete knowledge source "${source.title}"?`)) return;
    await api.delete(`/ai-agents/${route.params.id}/knowledge-base/${source.id}`);
    if (expandedSourceId.value === source.id) {
        expandedSourceId.value = null;
        ingestedPages.value = [];
        ingestedPageSearch.value = "";
        discoveredPages.value = [];
        discoveredPageSearch.value = "";
    }
    await loadSources();
}

async function indexSelectedDiscoveredPages(source) {
    const selectedPageIds = discoveredPages.value.filter((page) => page.isSelected).map((page) => page.id);
    if (selectedPageIds.length === 0) return;
    isIndexingSelectedPages.value = true;
    try {
        await api.post(`/ai-agents/${route.params.id}/knowledge-base/${source.id}/index-selected`, {selectedPageIds});
        await loadSources();
    } finally {
        isIndexingSelectedPages.value = false;
    }
}

async function createWebSource() {
    let finalLimit = 0;
    if (webForm.crawlMode === "page") finalLimit = 1;
    else if (webForm.crawlMode === "custom") finalLimit = webForm.customCrawlLimit || 1;

    const payload = {
        url: webForm.url,
        title: webForm.title,
        crawlPageLimit: finalLimit,
        reviewBeforeIndex: webForm.reviewBeforeIndex,
        schoolId: newSourceSchoolId.value,
    };

    await api.post(`/ai-agents/${route.params.id}/knowledge-base/web-url`, payload);
    webForm.url = "";
    webForm.title = "";
    webForm.crawlMode = "all";
    webForm.customCrawlLimit = null;
    webForm.reviewBeforeIndex = false;
    newSourceSchoolId.value = null;
    isNewSourceModalOpen.value = false;
    await loadSources();
}

async function createDocSource() {
    const file = docFileInput.value?.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", docForm.title);
    if (newSourceSchoolId.value !== null) formData.append("schoolId", String(newSourceSchoolId.value));

    await api.post(`/ai-agents/${route.params.id}/knowledge-base/document`, formData, {
        headers: {"Content-Type": "multipart/form-data"},
    });

    docForm.title = "";
    newSourceSchoolId.value = null;
    if (docFileInput.value) docFileInput.value.value = "";
    isNewSourceModalOpen.value = false;
    await loadSources();
}

async function createArticleSource() {
    await api.post(`/ai-agents/${route.params.id}/knowledge-base/manual-text`, {...articleForm, schoolId: newSourceSchoolId.value});
    newSourceSchoolId.value = null;
    articleForm.title = "";
    articleForm.content = "";
    isNewSourceModalOpen.value = false;
    await loadSources();
}

async function saveAgent() {
    agent.value = (await api.put(`/ai-agents/${route.params.id}`, buildAgentPayload(agent.value))).data;
    alert("Profile saved successfully.");
}

function buildAgentPayload(source) {
    const gdsEnabled = Boolean(source.gdsEnabled);
    const gdsType = gdsEnabled ? source.gdsType || "EducationAgency" : null;
    return {
        name: source.name,
        description: source.description,
        welcomeMessage: source.welcomeMessage,
        tone: source.tone,
        llmProvider: source.llmProvider,
        embeddingProvider: source.embeddingProvider,
        gdsEnabled,
        gdsAcademicEnabled: Boolean(source.gdsAcademicEnabled),
        gdsType,
        eduAgencyId: gdsEnabled && gdsType === "EducationAgency" ? Number(source.eduAgencyId) || null : null,
        schoolId: gdsEnabled && gdsType === "School" ? Number(source.schoolId) || null : null,
        isActive: source.isActive,
    };
}

function ensureGdsDefaults(source) {
    if (source?.gdsEnabled && !source.gdsType) {
        source.gdsType = "EducationAgency";
    }
}

function resetChat() {
    chatMessages.value = [];
    chatSessionId.value = null;
    chatVisitorId.value = crypto.randomUUID();
    chatInput.value = "";
}

async function sendMessage() {
    if (!chatInput.value.trim() || isChatLoading.value) return;

    const messageText = chatInput.value.trim();
    chatInput.value = "";
    chatMessages.value.push({role: "user", content: messageText});

    isChatLoading.value = true;
    scrollToBottom();

    try {
        const payload = {
            message: messageText,
            chatSessionId: chatSessionId.value,
            visitorId: chatVisitorId.value,
            sessionContext: {
                ip: (await geolocationPromise)?.ip || null,
                userAgent: navigator.userAgent,
                browserLanguage: navigator.language || null,
                geolocation: buildGeolocationContext(await geolocationPromise),
                pageUrl: window.location.href,
                pageTitle: document.title,
            },
        };

        // Call the public agent endpoint
        const response = await api.post(`/public/agents/${route.params.id}/chat`, payload);

        chatVisitorId.value = response.data.visitorId || chatVisitorId.value;
        chatSessionId.value = response.data.chatSessionId;
        chatMessages.value.push({role: "bot", content: response.data.message, academicPrograms: response.data.academic_programs || response.data.academicPrograms || [], languagePrograms: response.data.language_programs || []});
    } catch (error) {
        console.error("Chat error:", error);
        chatMessages.value.push({role: "bot", content: "Sorry, I encountered an error while processing your request."});
    } finally {
        isChatLoading.value = false;
        scrollToBottom();
    }
}

function scrollToBottom() {
    nextTick(() => {
        if (chatBody.value) {
            chatBody.value.scrollTop = chatBody.value.scrollHeight;
        }
    });
}

function buildGeolocationContext(geolocation) {
    if (!geolocation) {
        return {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
        };
    }

    return {
        country: geolocation.country || null,
        countryCode: geolocation.countryCode || null,
        city: geolocation.city || null,
        state: geolocation.state || null,
        district: geolocation.district || null,
        zipcode: geolocation.zipcode || null,
        latitude: geolocation.latitude || null,
        longitude: geolocation.longitude || null,
        timezone: geolocation.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || null,
        continentCode: geolocation.continentCode || null,
        continentName: geolocation.continentName || null,
        countryFlag: geolocation.countryFlag || null,
        countryEmoji: geolocation.countryEmoji || null,
        callingCode: geolocation.callingCode || null,
        tld: geolocation.tld || null,
        currencyCode: geolocation.currencyCode || null,
        currencyName: geolocation.currencyName || null,
        currencySymbol: geolocation.currencySymbol || null,
        timezoneOffset: geolocation.timezoneOffset !== undefined && geolocation.timezoneOffset !== null ? String(geolocation.timezoneOffset) : null,
    };
}

async function loadGeolocation() {
    try {
        const response = await fetch("https://api.ipgeolocation.io/v3/ipgeo?apiKey=7f8d9b5a64364aaea77557bb5cd4cded");
        if (!response.ok) return null;
        const data = await response.json();
        const location = data.location || {};
        const countryMetadata = data.country_metadata || {};
        const currency = data.currency || {};
        const timeZone = data.time_zone || {};

        return {
            ip: data.ip,
            country: location.country_name,
            countryCode: location.country_code2,
            city: location.city,
            state: location.state_prov,
            district: location.district,
            zipcode: location.zipcode,
            latitude: location.latitude,
            longitude: location.longitude,
            timezone: timeZone.name,
            continentCode: location.continent_code,
            continentName: location.continent_name,
            countryFlag: location.country_flag,
            countryEmoji: location.country_emoji,
            callingCode: countryMetadata.calling_code,
            tld: countryMetadata.tld,
            currencyCode: currency.code,
            currencyName: currency.name,
            currencySymbol: currency.symbol,
            timezoneOffset: timeZone.offset_with_dst ?? timeZone.offset,
        };
    } catch {
        return null;
    }
}

onMounted(load);
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 5000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(15, 23, 42, 0.48);
}

.modal {
    width: min(720px, 92vw);
    max-height: min(86vh, 900px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-sm);
    background: var(--bg-panel);
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
}

.modal-header,
.modal-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 18px;
    border-bottom: 1px solid var(--border-subtle);
    background: var(--bg-panel);
}

.modal-header h2 {
    margin: 0;
    font-size: 18px;
}

.modal-body {
    overflow: auto;
    padding: 18px;
}

.modal-footer {
    justify-content: flex-end;
    border-top: 1px solid var(--border-subtle);
    border-bottom: 0;
}

@media (max-width: 720px) {
    .modal-overlay {
        align-items: flex-start;
        padding: 16px;
    }

    .modal {
        width: 100%;
        max-height: calc(100vh - 32px);
    }
}

/* ── Chat Simulation Header ─────────────────────────────── */
.chat-sim-header {
    padding: 16px 20px 12px;
    border-bottom: 1px solid var(--border-subtle);
    background: var(--bg-panel);
}

.chat-sim-header-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
}

.chat-sim-title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
}

.chat-sim-desc {
    margin: 0;
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1.5;
}

/* ── Chat Messages ──────────────────────────────────────── */
.chat-message {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 4px 0;
}

.chat-message.user {
    flex-direction: row-reverse;
}

.chat-message-avatar {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
}

.bot-avatar {
    background: #1c3654;
    color: #fff;
}

.bot-avatar-image {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
}

.chat-message-data {
    display: flex;
    flex-direction: column;
    max-width: 82%;
}

.chat-message-data.has-programs {
    max-width: 94%;
}

.chat-message.user .chat-message-data {
    align-items: flex-end;
}

.chat-bubble {
    padding: 9px 13px;
    border-radius: 14px;
    font-size: 13px;
    line-height: 1.55;
    word-break: break-word;
}

.chat-bubble.has-programs {
    padding: 14px;
    background: var(--bg-surface) !important;
    border: 1px solid var(--border-subtle) !important;
}

.chat-message.bot .chat-bubble {
    background: var(--bg-surface);
    border: 1px solid var(--border-subtle);
    border-bottom-left-radius: 4px;
    color: var(--text-primary);
}

.chat-message.user .chat-bubble {
    background: #1c3654;
    color: #fff;
    border-bottom-right-radius: 4px;
}

.chat-bubble.thinking {
    opacity: 0.6;
}

/* Markdown content inside bubbles */
.chat-bubble :deep(p) {
    margin: 0 0 6px;
}
.chat-bubble :deep(p:last-child) {
    margin-bottom: 0;
}
.chat-bubble :deep(ul),
.chat-bubble :deep(ol) {
    margin: 4px 0;
    padding-left: 18px;
}
.chat-bubble :deep(li) {
    margin-bottom: 2px;
}
.chat-bubble :deep(code) {
    font-family: monospace;
    font-size: 12px;
    background: rgba(0, 0, 0, 0.12);
    padding: 1px 4px;
    border-radius: 3px;
}
.chat-bubble :deep(pre) {
    margin: 6px 0;
    background: rgba(0, 0, 0, 0.15);
    padding: 8px;
    border-radius: 6px;
    overflow-x: auto;
}
.chat-bubble :deep(pre code) {
    background: none;
    padding: 0;
}
.chat-bubble :deep(strong) {
    font-weight: 600;
}
.chat-bubble :deep(a) {
    text-decoration: underline;
    opacity: 0.85;
}

.chat-bubble :deep(.program-message-text) {
    margin: 0 0 12px;
    white-space: pre-wrap;
}

.chat-bubble :deep(.program-message-text:last-child) {
    margin: 12px 0 0;
}

.chat-bubble :deep(.program-card-list) {
    display: grid;
    gap: 14px;
    margin: 12px 0;
}

.chat-bubble :deep(.language-quote-warning) { color: #92400e; background: #fffbeb; padding: 6px 8px; border-radius: 5px; }
.chat-bubble :deep(.language-program-card .program-logo) { grid-template-columns: 60px minmax(0, 1fr); row-gap: 6px; }
.chat-bubble :deep(.language-program-card .program-logo > img),
.chat-bubble :deep(.language-program-card .program-logo-placeholder) { grid-column: 1; grid-row: 1 / span 2; }
.chat-bubble :deep(.language-program-card .program-logo-name) { grid-column: 2; grid-row: 1; }
.chat-bubble :deep(.language-program-card .program-logo-number) { grid-column: 2; grid-row: 2; max-width: 100%; justify-self: start; white-space: normal; }

.chat-bubble :deep(.program-code-block) {
    display: block;
    position: relative;
    background-color: #ffffff;
    border: 1px solid #e4e7ef;
    border-radius: 16px;
    padding: 18px 20px;
    margin-bottom: 0;
    font-family: Poppins, Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 12px;
    color: #333;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.chat-bubble :deep(.program-logo) {
    display: grid;
    grid-template-columns: 60px minmax(0, 1fr) auto;
    align-items: center;
    gap: 14px;
    margin-bottom: 14px;
    text-decoration: none;
    color: inherit;
}

.chat-bubble :deep(.program-logo-image),
.chat-bubble :deep(.program-logo-placeholder) {
    width: 60px;
    height: 60px;
    object-fit: contain;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: 1px solid var(--border-subtle);
    color: #94a3b8;
    font-size: 10px;
    font-weight: 700;
    text-align: center;
    flex: 0 0 auto;
}

.chat-bubble :deep(.program-logo-number) {
    align-self: start;
    max-width: 110px;
    border-radius: 999px;
    background: #f0efff;
    color: #6366f1;
    padding: 5px 12px;
    font-size: 11px;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.chat-bubble :deep(.program-logo-name) {
    min-width: 0;
}

.chat-bubble :deep(.program-school-name) {
    display: block;
    font-size: 13px;
    font-weight: 700;
    color: #222;
}

.chat-bubble :deep(.program-location) {
    display: block;
    font-size: 12px;
    color: #6b7280;
}

.chat-bubble :deep(.program-name) {
    display: block;
    font-size: 17px;
    line-height: 1.35;
    font-weight: 700;
    color: #252525;
    margin: 4px 0 8px;
}

.chat-bubble :deep(.program-detail) {
    margin: 1px 0;
}

.chat-bubble :deep(.program-code-block strong) {
    font-size: 12px;
    font-weight: 700;
    color: #333;
}

.chat-bubble :deep(.program-description) {
    margin-top: 8px;
    color: #374151;
}

.chat-bubble :deep(.program-description-toggle) {
    color: #6366f1;
    font-weight: 700;
    text-decoration: none;
    margin-left: 2px;
    opacity: 1;
}

.chat-bubble :deep(.program-link-btn) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: #6366f1;
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    text-decoration: none;
    padding: 10px 18px;
    margin-top: 16px;
    border-radius: 999px;
    transition: background-color 0.2s ease;
    opacity: 1;
}

.chat-bubble :deep(.program-link-btn:hover) {
    background-color: #4f46e5;
}

/* ── Chat Input Area ────────────────────────────────────── */
.chat-input-area {
    padding: 12px 16px 16px;
    background: var(--bg-panel);
    border-top: 1px solid var(--border-subtle);
}

.chat-input-box {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--bg-surface);
    border: 1.5px solid var(--border-subtle);
    border-radius: 12px;
    padding: 6px 6px 6px 14px;
    transition: border-color 0.15s;
}

.chat-input-box:focus-within {
    border-color: var(--accent-primary);
}

.chat-input-box input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    font-size: 13px;
    color: var(--text-primary);
    padding: 0;
}

.chat-input-box input::placeholder {
    color: var(--text-muted);
}

.chat-send-btn {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: var(--accent-primary);
    color: #fff;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.15s;
    line-height: 1;
}

.chat-send-btn:disabled {
    opacity: 0.35;
    cursor: default;
}

.chat-send-btn:not(:disabled):hover {
    opacity: 0.85;
}
</style>
